import { CalendarEvent } from '@/components/calendar/Calendar';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { useDaysInMonth } from '@/services/settings/useDates';
import { useMemo } from 'react';
import { RosterOption, useRostersForMonth } from '../providers/RosterOptionsService';
import { useCheckDateOptimising } from '@/services/optimiser/useOptimiserJobs';

export enum RosterStatus {
  optimising = 'Optimising',
  published = 'Published',
  republished = 'Republished',
  draft = 'Draft'
}

export interface RosterCalendarContextObject {
  id?: string;
  rosterStatus?: RosterStatus;
  numberOfEmployees?: number;
  numberOfHoursWorked?: number;
  numberOfErrors?: number;
  hoverOnly?: boolean;
  onClick?: () => void;
  onDeleteClick?: () => void;
}

export const useRosterCalendarEvents = (
  date: Date,
  onCreate: (date: Date) => void,
  onDelete: (option: RosterOption) => void,
  onSelect: (option: RosterOption) => void
) => {
  const dates = useDaysInMonth(date);
  const rosterOptions = useRostersForMonth();
  const checkDateOptimising = useCheckDateOptimising();

  return useMemo(() => {
    return dates.map(date => {
      const isOptimising = checkDateOptimising(date);

      if (isOptimising) {
        const optimiserCalendarEvent: CalendarEvent<RosterCalendarContextObject> = {
          date,
          contextObject: {
            rosterStatus: RosterStatus.optimising
          }
        };

        return optimiserCalendarEvent;
      }

      const rosterOption = rosterOptions.find(r => r.rosterDate === dateToApiFormat(date));

      const calendarEvent: CalendarEvent<RosterCalendarContextObject> = {
        date,
        contextObject: {
          id: rosterOption?.id,
          rosterStatus: rosterOption?.status as RosterStatus,
          onClick: rosterOption ? () => onSelect(rosterOption) : () => onCreate(date),
          onDeleteClick: rosterOption ? () => onDelete(rosterOption) : undefined,
          hoverOnly: !rosterOption
        }
      };

      return calendarEvent;
    });
  }, [rosterOptions, dates, checkDateOptimising]);
};
